import React, { useState, useEffect } from 'react';
import { useUserContext } from '../config/UserContext';
import { useKeycloak } from '../config/KeycloakContext';
import { useParams } from 'react-router-dom';

interface LastChanceReviewCardProps {
  onBack?: () => void;
  onGenerateDocument: () => void;
  generating?: boolean;
}

const ShadowDOMPreview: React.FC<{ html: string }> = ({ html }) => {
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (containerRef.current) {
      if (!containerRef.current.shadowRoot) {
        containerRef.current.attachShadow({ mode: 'open' });
      }
      containerRef.current.shadowRoot.innerHTML = html;
    }
  }, [html]);

  return <div ref={containerRef} className="w-full h-full min-h-[11in]" style={{ border: 'none', pointerEvents: 'none' }} />;
};

const LastChanceReviewCard: React.FC<LastChanceReviewCardProps> = ({ onBack, onGenerateDocument, generating }) => {
  const { apiBaseUrl } = useUserContext();
  const { getToken } = useKeycloak();
  const { documentId } = useParams<{ documentId: string }>();
  const [htmlContent, setHtmlContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isMounted = true;
    const fetchPreview = async () => {
      try {
        const token = getToken ? await getToken() : null;
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const res = await fetch(`${apiBaseUrl}/api/frozendeed/document/${documentId}/generate?preview=true`, {
          method: 'POST',
          headers
        });

        if (!res.ok) {
          throw new Error('Failed to load preview');
        }

        const html = await res.text();
        if (isMounted) {
          setHtmlContent(html);
          setLoading(false);
        }
      } catch (err: any) {
        if (isMounted) {
          setError(err.message || 'Unknown error');
          setLoading(false);
        }
      }
    };

    fetchPreview();
    return () => { isMounted = false; };
  }, [documentId, apiBaseUrl, getToken]);

  return (
    <div className="flex flex-col w-full mx-auto font-plus-jakarta pb-20">
      {/* Print Warning Styles */}
      <style>
        {`
          @media print {
            .preview-container { display: none !important; }
            .print-warning { display: block !important; font-size: 24px; font-weight: bold; text-align: center; margin-top: 2in; }
          }
        `}
      </style>
      <div className="print-warning hidden">Please generate the final document to print.</div>

      {/* Header */}
      <div className="bg-white p-6 rounded-t-xl border border-[#D7DFEC] text-center preview-container max-w-4xl mx-auto w-full">
        <h2 className="text-2xl font-bold text-[#141435] mb-2">Last Chance Review</h2>
        <p className="text-[#485D7D]">
          Please review your final document below. Once generated, no further changes can be made.
        </p>
      </div>

      {/* Document Preview Area */}
      <div className="bg-[#E5E7EB] p-4 sm:p-8 preview-container border-l border-r border-b border-[#D7DFEC] max-w-4xl mx-auto w-full rounded-b-xl shadow-inner relative">
        {loading ? (
          <div className="bg-white w-full max-w-[8.5in] h-[11in] mx-auto shadow-xl flex items-center justify-center rounded">
            <div className="flex flex-col items-center gap-4 text-gray-500">
              <svg className="animate-spin h-8 w-8 text-[#0B4BB6]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Loading exact preview...</span>
            </div>
          </div>
        ) : error ? (
          <div className="bg-white w-full max-w-[8.5in] h-[11in] mx-auto shadow-xl flex items-center justify-center rounded">
            <span className="text-red-500">Failed to load preview: {error}</span>
          </div>
        ) : (
          <div className="bg-white w-full max-w-[8.5in] min-h-[11in] mx-auto shadow-xl rounded overflow-hidden relative">
             {/* DRAFT WATERMARK OVERLAY */}
             <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-50 overflow-hidden">
                <div className="text-[80px] text-red-600 opacity-20 -rotate-45 whitespace-nowrap font-bold select-none font-sans">
                    DRAFT - NOT VALID FOR RECORDING
                </div>
             </div>
             <ShadowDOMPreview html={htmlContent} />
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex flex-row w-full max-w-[700px] gap-4 mt-8 mx-auto preview-container">
        <button
          type="button"
          className="flex-1 bg-[#7C8B9D] text-white font-plus-jakarta font-bold text-[15px] py-[12px] px-5 rounded-full hover:bg-[#485D7D] transition-colors"
          onClick={onBack}
          disabled={generating}
        >
          Back to Summary
        </button>
        <button
          type="button"
          className={`flex-1 bg-[#0B4BB6] text-white font-plus-jakarta font-bold text-[15px] py-[12px] px-5 rounded-full transition-colors ${generating ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#23235a]'}`}
          onClick={onGenerateDocument}
          disabled={generating || loading}
        >
          {generating ? 'Generating Final Document...' : 'Looks Good — Generate Document!'}
        </button>
      </div>
    </div>
  );
};

export default LastChanceReviewCard;
